<script lang="ts" setup>import { computed as _computed } from 'vue';

import { Heading, RadioSelect } from '@pohlcon/design-system';
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { WallStructure } from '~/models';
import { useWallStructuresStore } from '~/store';
import SelectedWallStructure from './WallStructureCard.vue';

const { t } = useI18n();

interface Props {
  modelValue: WallStructure | null;
}

const props = defineProps<Props>();

defineEmits<{
  (e: 'update:modelValue', model: WallStructure | null): void,
}>();

const store = useWallStructuresStore();

const wallThicknesses = _computed(() => {
  const thicknesses = props.modelValue?.thicknesses;
  if (!thicknesses) return null;
  return Object.entries(thicknesses)
    .map(([text, value]) => ({ text: `${value} mm`, value }));
});

const wallStructures = _computed(() =>
  store.wallStructures);

const selectedWallThickness = computed({
  get () {
    return store.selectedThickness;
  },
  set (value: number) {
    store.selectedThickness = value;
  }
});

watch(() => [props.modelValue], () => {
  store.selectedThickness = 0;
})

</script>

<template>
  <div class="wall-selection wrapper">
    <span class="sub-header" style="grid-area: title">{{ t('selectView.insType.subtitle') }}</span>
    <div class="wall-selection__structure container" style="grid-area: structure" cypress-id="wall-structure-container">
      <SelectedWallStructure v-for="(wallStructure, i) in wallStructures" :key="i" :wallStructure="wallStructure"
        :class="{ active: wallStructure === modelValue }" @selectedWallStructure="$emit('update:modelValue', $event)"
         />
    </div>
    <div class="wall-selection__thickness" v-if="wallThicknesses" style="grid-area: thickness">
      <Heading class="subtitle" :level="5" cypress-id="wall-structure-thickness-heading">{{ t('selectView.wallThickness.subtitle') }}</Heading>
      <div class="container">
        <RadioSelect :options="wallThicknesses" v-model="selectedWallThickness" />
      </div>
    </div>
  </div>
</template>

<style lang="scss">
@use '../../../_variables' as *;

.wall-selection {
  display: grid;
  grid-template:
    "title title" max-content
    "structure thickness" 1fr / 1fr;

  @media screen and (max-width: $breakpoint-thickness) {
    grid-template:
      "title" max-content
      "structure" 1fr
      "thickness" max-content / 1fr;
    row-gap: 1rem;


    &__thickness {
      margin-top: 1rem;
      max-width: 50%;
    }
  }


  &__structure {
    display: grid;
    justify-content: space-evenly;
    justify-items: center;
    grid-auto-flow: column;
    align-items: center;
    grid-gap: 20px;

    @media screen and (max-width: $breakpoint-nav) {
      grid-auto-flow: row;
      grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    }
  }

  &__thickness {
    min-width: 150px;
  }

  &.wrapper {
    margin-bottom: var(--gutter-xl_plus);
  }
}

.sub-header {
  margin-bottom: var(--gutter-xl_plus);
}

.button-wrapper {
  margin-top: 1rem;
}
</style>
